import { SiteHeader } from "@/components/site-header"

export default function OperatorGuidePage() {
  return (
    <>
      <SiteHeader title="Guía del Operador" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="w-full space-y-8">

          {/* ─── Acceso ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Acceso al sistema</h2>
            <p className="text-sm text-muted-foreground">
              Ingresá con tu email y contraseña en la pantalla de inicio. Tu sesión dura{" "}
              <span className="font-medium">8 horas</span>. Al vencerse, el sistema muestra un aviso
              y te pide que inicies sesión nuevamente — no se cierra la pantalla de forma abrupta.
            </p>
            <div className="mt-3 bg-muted rounded-lg px-4 py-3 text-sm space-y-1">
              <div><span className="font-medium">Aviso de sesión expirada:</span> si ves un cuadro de diálogo con el mensaje "Tu sesión ya no es válida", hacé click en <span className="font-medium">Ir al login</span> para volver a ingresar.</div>
            </div>
          </section>

          {/* ─── Login fallido ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Intentos de login fallidos</h2>
            <p className="text-sm text-muted-foreground">
              Si ingresás una contraseña incorrecta 5 veces seguidas, tu dirección IP quedará bloqueada por{" "}
              <span className="font-medium">15 minutos</span>. Pasado ese tiempo podés volver a intentarlo.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Si necesitás acceso urgente antes de ese tiempo, contactá a un administrador para que reinicie el servicio.
            </p>
          </section>

          {/* ─── Cerrar sesión ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Cerrar sesión</h2>
            <p className="text-sm text-muted-foreground">
              Siempre cerrá sesión cuando termines de trabajar, especialmente desde equipos compartidos.
              Hacé click en tu nombre en la parte inferior del sidebar y seleccioná{" "}
              <span className="font-medium">Cerrar sesión</span>.
            </p>
          </section>

          {/* ─── Navegación ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Qué podés ver en el sistema</h2>
            <p className="text-sm text-muted-foreground mb-3">
              Como operador tenés acceso de <span className="font-medium">lectura</span> a todos los módulos.
              Los botones de crear, editar y eliminar son exclusivos del rol administrador.
            </p>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Sección en el sidebar</th>
                    <th className="text-left px-4 py-2 font-medium">Contenido</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr>
                    <td className="px-4 py-2 font-medium">Dashboard</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">Indicadores generales del sistema</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Geografía → Países</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">Listado de países configurados (Mercosur + CL + BO)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Geografía → Provincias</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">~71 provincias y estados; filtro por país</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Geografía → Ciudades y Trayectorias</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">~1560 localidades; también incluye trayectos logísticos multi-ciudad</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Geografía → Rutas</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">Rutas con ciudad origen y ciudad destino, km opcionales</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Viajes → Tipos de servicio</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">Clasificación de viajes: CF (Int'l), CN (Nacional), RT (Round Trip), VV (Vuelta Vacío)…</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Cargas → Productos</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">Catálogo de mercadería: ACEITE, CARNE, CONGELADOS, LECHE, VACUNAS…</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Cargas → Tipos de carga</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">Clasificación de alto nivel: DE (Devoluciones), LT (LATA), NP (No Perecedero), PE (Perecedero)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* ─── Búsqueda y filtros ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Buscar y filtrar registros</h2>
            <p className="text-sm text-muted-foreground mb-3">
              Todas las páginas de listado incluyen buscador y filtros. Los resultados se paginan de a 25 registros.
            </p>
            <div className="space-y-2 text-sm">
              <div className="flex gap-2">
                <span className="font-medium w-32 shrink-0">Buscador:</span>
                <span className="text-muted-foreground">filtra por nombre y código simultáneamente mientras escribís.</span>
              </div>
              <div className="flex gap-2">
                <span className="font-medium w-32 shrink-0">Filtro de estado:</span>
                <span className="text-muted-foreground"><span className="font-mono text-xs bg-muted px-1 rounded">Activos</span> muestra los registros en uso. <span className="font-mono text-xs bg-muted px-1 rounded">Eliminados</span> muestra los borrados temporalmente. <span className="font-mono text-xs bg-muted px-1 rounded">Todos</span> combina ambos.</span>
              </div>
              <div className="flex gap-2">
                <span className="font-medium w-32 shrink-0">Filtros extra:</span>
                <span className="text-muted-foreground">Provincias y Ciudades permiten filtrar además por país y provincia.</span>
              </div>
            </div>
          </section>

          {/* ─── Soft delete ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Registros eliminados (borrado lógico)</h2>
            <p className="text-sm text-muted-foreground mb-3">
              En este sistema ningún dato se borra definitivamente. Cuando un administrador elimina un registro,
              queda marcado como <span className="font-medium">eliminado</span> pero se conserva en la base de datos.
            </p>
            <p className="text-sm text-muted-foreground">
              Podés ver los registros eliminados usando el filtro{" "}
              <span className="font-mono text-xs bg-muted px-1 rounded">Eliminados</span> en cualquier listado.
              Aparecen con menor opacidad que los activos. Solo los administradores pueden restaurarlos.
            </p>
          </section>

          {/* ─── Tu rol ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Tu rol</h2>
            <p className="text-sm text-muted-foreground">
              Como operador tenés acceso de lectura a Geografía, Viajes y Cargas.
              No podés crear, editar ni eliminar registros — eso es exclusivo de los administradores.
              Si necesitás permisos adicionales, solicitalo a un administrador.
            </p>
          </section>

        </div>
      </div>
    </>
  )
}
